import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UwIcon } from '../../shared/ui/icon/uw-icon';
import {
  ClassicEnrichmentPage,
  ClassicRulesPage,
  ClassicPortfolioPage,
  ClassicAnalyticsPage,
  ClassicCollaborationPage,
  ClassicCompliancePage,
} from './portfolio-pages';
import {
  ClassicProfilePage,
  ClassicRiskPage,
  ClassicPricingPage,
  ClassicReferralsPage,
  ClassicDocumentsPage,
  ClassicQuotePage,
} from './account-pages';

const SECTIONS: Record<string, { title: string; icon: string }> = {
  profile: { title: 'Customer Risk Profile', icon: 'building' },
  risk: { title: 'Risk Assessment Engine', icon: 'gauge' },
  pricing: { title: 'Pricing & Rating', icon: 'calculator' },
  referrals: { title: 'Referral & Approval', icon: 'arrow-up' },
  quote: { title: 'Quote & Policy Integration', icon: 'file-check' },
  portfolio: { title: 'Portfolio & Exposure', icon: 'globe' },
  analytics: { title: 'Analytics & Dashboards', icon: 'chart' },
  documents: { title: 'Document Management', icon: 'document' },
  enrichment: { title: 'Data Enrichment', icon: 'sparkles' },
  rules: { title: 'Rules & Decision Engine', icon: 'check-circle' },
  collaboration: { title: 'Collaboration Tools', icon: 'chat' },
  compliance: { title: 'Compliance & Audit', icon: 'shield' },
};

@Component({
  selector: 'uw-classic-section-page',
  standalone: true,
  imports: [
    UwIcon,
    ClassicEnrichmentPage,
    ClassicRulesPage,
    ClassicPortfolioPage,
    ClassicAnalyticsPage,
    ClassicCollaborationPage,
    ClassicCompliancePage,
    ClassicProfilePage,
    ClassicRiskPage,
    ClassicPricingPage,
    ClassicReferralsPage,
    ClassicDocumentsPage,
    ClassicQuotePage,
  ],
  template: `
    <div class="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
      <button
        class="inline-flex items-center gap-1 text-[11px] font-semibold text-text-muted transition-colors hover:text-ai"
        (click)="back()"
      >
        <uw-icon name="arrow-left" [size]="12" />
        Back to submissions
      </button>

      <header class="flex items-center gap-2">
        <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-soft text-primary">
          <uw-icon [name]="meta().icon" [size]="15" />
        </span>
        <div>
          <h1 class="typo-heading text-primary">{{ meta().title }}</h1>
          <p class="text-[11.5px] text-text-muted">Classic workbench · Underwriting desk</p>
        </div>
      </header>

      <div>
        @switch (sectionId()) {
          @case ('profile') {
            <uw-classic-profile-page />
          }
          @case ('risk') {
            <uw-classic-risk-page />
          }
          @case ('pricing') {
            <uw-classic-pricing-page />
          }
          @case ('referrals') {
            <uw-classic-referrals-page />
          }
          @case ('quote') {
            <uw-classic-quote-page />
          }
          @case ('enrichment') {
            <uw-classic-enrichment-page />
          }
          @case ('rules') {
            <uw-classic-rules-page />
          }
          @case ('portfolio') {
            <uw-classic-portfolio-page />
          }
          @case ('analytics') {
            <uw-classic-analytics-page />
          }
          @case ('documents') {
            <uw-classic-documents-page />
          }
          @case ('collaboration') {
            <uw-classic-collaboration-page />
          }
          @case ('compliance') {
            <uw-classic-compliance-page />
          }
          @default {
            <uw-classic-portfolio-page />
          }
        }
      </div>
    </div>
  `,
})
export class ClassicSectionPage {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly sectionId = signal<string>('portfolio');

  protected readonly meta = computed<{ title: string; icon: string }>(() => SECTIONS[this.sectionId()] ?? SECTIONS['portfolio']);

  constructor() {
    this.route.paramMap.subscribe((params) => {
      this.sectionId.set(params.get('section') ?? 'portfolio');
    });
  }

  protected back(): void {
    void this.router.navigate(['/classic/submissions']);
  }
}
